
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Key, Shield, Globe, Layers } from 'lucide-react';
import { Button } from '../../ui/Button';

interface CreateKeyModalProps {
    onClose: () => void;
    onCreate: (data: any) => void;
}

export const CreateKeyModal: React.FC<CreateKeyModalProps> = ({ onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [chain, setChain] = useState('ALL');
    const [scopes, setScopes] = useState<string[]>(['read:payments']);
    const [rateLimit, setRateLimit] = useState(1000);

    const availableScopes = [
        { id: 'read:payments', label: 'Read Payments' },
        { id: 'write:payments', label: 'Create Payments' },
        { id: 'write:escrow', label: 'Create Escrows' },
        { id: 'admin:all', label: 'Admin Access (Dangerous)', danger: true },
    ];

    const toggleScope = (scopeId: string) => {
        setScopes(prev => 
            prev.includes(scopeId) 
            ? prev.filter(s => s !== scopeId) 
            : [...prev, scopeId]
        );
    };

    const handleCreate = () => {
        onCreate({ name, chain, scopes, rateLimit, usage: 0 });
        onClose();
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div 
                initial={{ scale: 0.95, y: 10 }} 
                animate={{ scale: 1, y: 0 }} 
                exit={{ scale: 0.95, y: 10 }} 
                className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg border border-stone-200 dark:border-[#2D3142] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6 border-b border-stone-100 dark:border-white/5 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Key size={20} className="text-nobel-gold" />
                        <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white">Create API Key</h3>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Key Name</label>
                        <input 
                            type="text" 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Production POS Server"
                            className="w-full p-3 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl outline-none focus:border-nobel-gold text-stone-900 dark:text-white"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Chain Scope</label>
                            <div className="relative">
                                <select 
                                    value={chain} 
                                    onChange={(e) => setChain(e.target.value)}
                                    className="w-full p-3 pl-10 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl outline-none focus:border-nobel-gold text-stone-900 dark:text-white appearance-none"
                                >
                                    <option value="ALL">All Chains</option>
                                    <option value="SOL">Solana Only</option>
                                    <option value="SUI">Sui Only</option>
                                    <option value="EVM">EVM Compatible</option>
                                </select>
                                <Layers size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"/>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Rate Limit (Daily)</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    value={rateLimit}
                                    onChange={(e) => setRateLimit(parseInt(e.target.value))}
                                    className="w-full p-3 pl-10 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl outline-none focus:border-nobel-gold text-stone-900 dark:text-white"
                                />
                                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"/>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-3">Permissions</label>
                        <div className="space-y-2">
                            {availableScopes.map(scope => (
                                <div 
                                    key={scope.id}
                                    onClick={() => toggleScope(scope.id)}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                        scopes.includes(scope.id) 
                                        ? 'bg-nobel-gold/10 border-nobel-gold text-stone-900 dark:text-white' 
                                        : 'bg-stone-50 dark:bg-white/5 border-transparent text-stone-500 hover:border-stone-200 dark:hover:border-white/10'
                                    }`}
                                >
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${scopes.includes(scope.id) ? 'bg-nobel-gold border-nobel-gold' : 'border-stone-400'}`}>
                                        {scopes.includes(scope.id) && <Shield size={12} className="text-white fill-white"/>}
                                    </div>
                                    <span className={`text-sm font-medium ${scope.danger ? 'text-red-500' : ''}`}>{scope.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-stone-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!name}>Generate Key</Button>
                </div>
            </motion.div>
        </motion.div>
    );
};
